import { get } from '@/api/client';
import { T } from '@/i18n';
import { today } from '@/rules';
import { Button, Empty, LoadError, Panel, Skeleton } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD, fmtDs, properName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Correction {
  field: 'start' | 'end';
  before: string | null;
  after: string | null;
  at: string;
  by: string | null;
  reason: string | null;
  deleted: boolean;
  /** written by somebody else afterwards rather than clocked as it happened */
  afterwards?: boolean;
}
interface Entry {
  date: string;
  shift_id: number;
  name: string | null;
  code: string;
  start: string;
  end: string | null;
  deleted: boolean;
  corrections: Correction[];
}
export interface StaffList {
  company: { name: string | null; org_number: string | null };
  from: string;
  to: string;
  entries: Entry[];
  codes: {
    code: string;
    name: string | null;
    employee_id?: string | null;
    personal_number?: string | null;
  }[];
}

const clock = (v: string | null) =>
  v
    ? new Intl.DateTimeFormat('nb-NO', {
        timeZone: 'Europe/Oslo',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(v))
    : '';
const stamp = (v: string) =>
  new Intl.DateTimeFormat('nb-NO', {
    timeZone: 'Europe/Oslo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(v));
const osloDate = (v: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date(v));
/** The end, with its date when it falls on another day than the start (a night, a clock-out closed later). */
const endText = (start: string, end: string) =>
  osloDate(end) === osloDate(start) ? clock(end) : `${clock(end)} ${fmtDs(osloDate(end))}`;
const nameOf = (n: string | null) => (n ? properName(n) : T('(slettet person)'));
const orgNo = (n: string) => n.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');

/** One correction in words: "Slutt 16:05 (var tom), Kari Kaffe 14.09.2026, 17:20. Kom senere" */
export function correctionText(c: Correction): string {
  const what = c.field === 'start' ? T('Start') : T('Slutt');
  if (c.afterwards)
    return `${what} ${clock(c.after)} ${T('registrert i etterkant av')} ${c.by ? properName(c.by) : T('ukjent')} ${stamp(c.at)}${c.reason ? `. ${c.reason}` : ''}`;
  const change = c.deleted
    ? T('slettet')
    : `${clock(c.after) || T('tom')} (${T('var')} ${clock(c.before) || T('tom')})`;
  return `${what} ${change}, ${c.by ? properName(c.by) : T('ukjent')} ${stamp(c.at)}${c.reason ? `. ${c.reason}` : ''}`;
}

/**
 * Personalliste (bokføringsforskriften § 8-5-6; plan 0027, food service first): who worked, from
 * and to, day by day in the order they started, with every correction and who made it. Printed
 * as it stands for an inspector; the code list is a separate print, kept with the list.
 */
export default function Personalliste() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  // ?liste=koder opens the code list straight away (Timer → Rapporter → Kodeliste)
  const [sp] = useSearchParams();
  const [codes, setCodes] = useState(sp.get('liste') === 'koder');
  const q = useQuery({
    queryKey: ['staff-list', from, to],
    queryFn: () => get<StaffList>(`/admin/staff-list?from=${from}&to=${to}`),
    enabled: from <= to,
  });
  const list = q.data;
  const backwards = from > to;
  const days = [...new Set((list?.entries ?? []).map((e) => e.date))];

  return (
    <div className="page personalliste">
      <div className="ph noprint" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
        <label className="fld" htmlFor="personalliste-from">
          <span>{T('Fra')}</span>
          <DateInput
            id="personalliste-from"
            className="inp"
            aria-label={T('Fra')}
            value={from}
            onValueChange={(value) => value && setFrom(value)}
          />
        </label>
        <label className="fld" htmlFor="personalliste-to">
          <span>{T('Til')}</span>
          <DateInput
            id="personalliste-to"
            className="inp"
            aria-label={T('Til')}
            value={to}
            onValueChange={(value) => value && setTo(value)}
          />
        </label>
        <Button
          onClick={() => {
            setFrom(today());
            setTo(today());
          }}
          ghost
        >
          {T('I dag')}
        </Button>
        <span className="sp" />
        <Button ghost aria-pressed={codes} onClick={() => setCodes(!codes)}>
          {codes ? T('Vis personallisten') : T('Kodeliste')}
        </Button>
        <Button tone="pri" onClick={() => window.print()}>
          <Icon name="document" />
          {T('Skriv ut')}
        </Button>
      </div>

      {backwards ? (
        <div className="al warn">{T('Til må være samme dag som Fra eller senere.')}</div>
      ) : q.isPending ? (
        <Skeleton rows={6} />
      ) : q.isError ? (
        <LoadError what={T('personallisten')} detail={q.error.message} onRetry={() => q.refetch()} />
      ) : (
        list && (
          <Panel
            title={codes ? T('Kodeliste til personallisten') : T('Personalliste')}
            sub={`${list.company.name ?? ''}${list.company.org_number ? `, org.nr. ${orgNo(list.company.org_number)}` : ''}, ${fmtD(list.from)}${list.to !== list.from ? `–${fmtD(list.to)}` : ''}`}
          >
            {!list.company.org_number && (
              <div className="al warn noprint">
                {T(
                  'Organisasjonsnummeret mangler. Legg det inn under Innstillinger → Bedrift; det skal stå på listen.',
                )}
              </div>
            )}
            {codes ? (
              <table className="t" data-testid="codes">
                <thead>
                  <tr>
                    <th>{T('Kode')}</th>
                    <th>{T('Navn')}</th>
                    <th>{T('Ansattnr')}</th>
                    {list.codes.some((c) => 'personal_number' in c) && (
                      <th>{T('Fødselsnummer / D-nummer')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {list.codes.map((c) => (
                    <tr key={c.code}>
                      <td>{c.code}</td>
                      <td>{nameOf(c.name)}</td>
                      <td>{c.employee_id ?? ''}</td>
                      {'personal_number' in c && <td>{c.personal_number || T('mangler')}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : days.length === 0 ? (
              <Empty>{T('Ingen har stemplet inn i perioden.')}</Empty>
            ) : (
              days.map((d) => (
                <table className="t" key={d} data-testid={`day-${d}`}>
                  <caption style={{ textAlign: 'left', fontWeight: 700 }}>{fmtD(d)}</caption>
                  <thead>
                    <tr>
                      <th>{T('Navn')}</th>
                      <th>{T('Kode')}</th>
                      <th>{T('Start')}</th>
                      <th>{T('Slutt')}</th>
                      <th>{T('Rettelser')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.entries
                      .filter((e) => e.date === d)
                      .map((e) => (
                        <tr key={`${e.shift_id}-${e.start}`}>
                          <td className={e.deleted ? 'struck' : undefined}>{nameOf(e.name)}</td>
                          <td className={e.deleted ? 'struck' : undefined}>{e.code}</td>
                          <td className={e.deleted ? 'struck' : undefined}>{clock(e.start)}</td>
                          <td className={e.deleted ? 'struck' : undefined}>
                            {e.end ? endText(e.start, e.end) : e.deleted ? '' : T('på jobb')}
                          </td>
                          <td>
                            {e.deleted && <b>{T('Slettet')}. </b>}
                            {e.corrections.map((c) => (
                              <div key={`${c.at}-${c.field}`}>{correctionText(c)}</div>
                            ))}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ))
            )}
            <p className="noprint" style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {T(
                'Alle som arbeider skal stå på listen, også eieren, innleide og ulønnede. Den som ikke stempler, kommer ikke med.',
              )}
            </p>
            <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {T(
                'Personalliste etter bokføringsforskriften § 8-5-6. Oppbevares i 3 år og 6 måneder etter regnskapsårets slutt, sammen med kodelisten.',
              )}
            </p>
          </Panel>
        )
      )}
    </div>
  );
}
